import { memo, useEffect, useState } from "react";
import { Grid } from "@material-ui/core";

import { Field, useFormikContext } from "formik";
import TextField from "@/components/textField/textField";
import { useBlur } from "@/pages/demo/hooks/useBlur";
import { buildRevealAnimationStyle } from "@/utils/fieldRevealAnimation";

import { FastFieldSafe } from "components/fastFieldSafe/fastFieldSafe";
import Select from "components/select/selectField";

import {
  DataCy,
  isntNorthAmerica,
  JobTitlesMarketing,
  MainPropertyTypeEnum,
  OtherJobTitles,
  WhenPlanToUseSoftware,
  DoorLoopLeadDto,
  YesNoEnum
} from "@/dto";
import {
  JOB_TITLE_MARKETING_TRANSLATIONS,
  MAIN_PROPERTY_TYPE_TRANSLATIONS,
  OTHER_JOB_TITLE_TRANSLATIONS,
  WHEN_PLAN_TO_USE_SOFTWARE_TRANSLATIONS
} from "@/pages/demo/translationObjects";

const customPropertyTypeEnumOrder = [
  "RESIDENTIAL",
  "COMMERCIAL",
  "RESIDENTIAL_COMMERCIAL",
  "COMMUNITY_ASSOCIATIONS",
  "AFFORDABLE_HOUSING",
  "STORAGE",
  "SHORT_TERM_RENTALS",
  "OTHER"
];

interface AllDetailsStepProps {
  shouldDisplayEmail: boolean;
}

const AllDetailsStepComponent = ({ shouldDisplayEmail }: AllDetailsStepProps) => {
  const { makeBlurTracking, makeDidBlur } = useBlur();
  const { values, setFieldValue } = useFormikContext<DoorLoopLeadDto>();

  const [isFirstStepValid, setIsFirstStepValid] = useState(false);

  const getRevealStyle = (): React.CSSProperties => buildRevealAnimationStyle({ delayMs: 250 });

  const handleSelectedJobTitleChange = (value: string) => {
    if (value !== JobTitlesMarketing.OTHER) {
      setFieldValue("jobTitle", undefined);
    }

    makeBlurTracking("user_selected_occupation", value);
  };

  useEffect(() => {
    const required = [values.name?.trim(), values.phone?.trim(), values.mainPropertyType];

    if (shouldDisplayEmail) required.push(values.email?.trim());
    setIsFirstStepValid(required.every(Boolean));
  }, [shouldDisplayEmail, values.email, values.name, values.phone, values.mainPropertyType]);

  return (
    <div className="mt-5 flex flex-col space-y-5">
      {shouldDisplayEmail && (
        <Grid item xs={12}>
          <Field
            component={TextField}
            label={"Email"}
            name={"email"}
            required
            autoComplete={"on"}
            onBlur={(value) => makeBlurTracking("email", value)}
            onBlurUnconditionally={(value) => makeDidBlur("email", value)}
            type={"email"}
            dataCy={DataCy.marketing.demoForm.personalInfoStep.emailInput}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Field
          component={TextField}
          label={"Full Name"}
          name={"name"}
          required
          autoComplete={"on"}
          onBlur={(value) => makeBlurTracking("full_name", value)}
          onBlurUnconditionally={(value) => makeDidBlur("name", value)}
          dataCy={DataCy.marketing.demoForm.personalInfoStep.fullNameInput}
        />
      </Grid>
      <Grid item xs={12}>
        <Field
          component={TextField}
          label={"Phone"}
          name={"phone"}
          required
          autoComplete={"on"}
          onBlur={(value) => makeBlurTracking("phone", value)}
          onBlurUnconditionally={(value) => makeDidBlur("phone", value)}
          maxNumber={100_000_000_000_000_000}
          type={"tel"}
          inputProps={{ inputMode: "tel" }}
          dataCy={DataCy.marketing.demoForm.personalInfoStep.phone}
        />
      </Grid>
      <Grid item xs={12}>
        <FastFieldSafe
          component={Select}
          name={"mainPropertyType"}
          onChange={(value) => makeBlurTracking("primary_portfolio__type", value)}
          label={"What is your main property type?"}
          selectionEnum={MainPropertyTypeEnum}
          customEnumOrder={customPropertyTypeEnumOrder}
          translationObject={MAIN_PROPERTY_TYPE_TRANSLATIONS}
          uniqueKey={"mainPropertyTypeSelector"}
          useEnumValue
          displayEmpty
          required
          dataCy={DataCy.marketing.demoForm.companyInfoStep.type}
        />
      </Grid>
      {isFirstStepValid && (
        <div className="space-y-5" style={getRevealStyle()}>
          <Grid item xs={12}>
            <Field
              component={TextField}
              label={"Number of Units"}
              name={"numberOfUnits"}
              required
              onBlur={(value) => makeBlurTracking("expected_number_of_units__c", value)}
              onBlurUnconditionally={(value) => makeDidBlur("numberOfUnits", value)}
              maxNumber={999_999}
              formatType="number"
              inputProps={{ inputMode: "numeric" }}
              type={"tel"}
              dataCy={DataCy.marketing.demoForm.personalInfoStep.numberOfUnitsInput}
            />
          </Grid>
          <Grid item xs={12}>
            <FastFieldSafe
              component={Select}
              name={"selectedJobTitle"}
              onChange={handleSelectedJobTitleChange}
              label={"What is your role?"}
              selectionEnum={JobTitlesMarketing}
              translationObject={JOB_TITLE_MARKETING_TRANSLATIONS}
              uniqueKey={"rolesSelector"}
              useEnumValue
              displayEmpty
              required
              dataCy={DataCy.marketing.demoForm.companyInfoStep.role}
            />
          </Grid>
          {values.selectedJobTitle === JobTitlesMarketing.OTHER && (
            <Grid item xs={12}>
              <FastFieldSafe
                component={Select}
                name={"jobTitle"}
                onChange={(value) => makeBlurTracking("are_you_a_tenant__attorney__or_accountant_", value)}
                label={"I am a..."}
                selectionEnum={OtherJobTitles}
                translationObject={OTHER_JOB_TITLE_TRANSLATIONS}
                uniqueKey={"otherTitlesSelector"}
                useEnumValue
                displayEmpty
                required
                dataCy={DataCy.marketing.demoForm.companyInfoStep.other}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <FastFieldSafe
              component={Select}
              name={"whenPlanToUseSoftware"}
              onChange={(value) => makeBlurTracking("customer_reported_timeline__c", value)}
              label={"When do you plan on using the software?"}
              selectionEnum={WhenPlanToUseSoftware}
              translationObject={WHEN_PLAN_TO_USE_SOFTWARE_TRANSLATIONS}
              uniqueKey={"whenPlanToUseSoftwareSelector"}
              useEnumValue
              displayEmpty
              required
              dataCy={DataCy.marketing.demoForm.companyInfoStep.WhenPlanToUseSoftware}
            />
          </Grid>
          {isntNorthAmerica(values.country || "") && (
            <Grid item xs={12}>
              <FastFieldSafe
                component={Select}
                name={"doYouManagePropertiesInTheUSOrCanada"}
                onChange={(value) => makeBlurTracking("manage_properties_us_canada", value)}
                label={"Do you manage properties in the US or Canada?"}
                selectionEnum={YesNoEnum}
                translationObject={{
                  [YesNoEnum.YES]: "Yes",
                  [YesNoEnum.NO]: "No"
                }}
                uniqueKey={"doYouManagePropertiesInTheUSOrCanadaSelector"}
                useEnumValue
                displayEmpty
                required
                dataCy="doYouManagePropertiesInTheUSOrCanada"
              />
            </Grid>
          )}
        </div>
      )}
    </div>
  );
};

export const AllDetailsStep = memo(AllDetailsStepComponent);
